import { useState, type ComponentType, type FC, useRef } from 'react'
import type { CompatibilityResultPopupType } from '../components/Popup/Powermate/types.ts'
import '../assets/styles/powermate.scss'
import { mountForShopify } from './mount'
import { scrollToElementLazy } from '../utils/scroll'
import { useMinHeights, type SectionHeight } from '../hooks/min-heights.ts'
import { getComponent } from '../layouts/components'
import LazySection from '../layouts/LazySection'
import ProductLayout from '../layouts/ProductLayout'
import PowermateBanner from '../components/Powermate/PowermateBanner'

import bannerDesktopBackground from '../assets/images/powermate/powermate_banner_desktop.png'
import bannerMobileBackground from '../assets/images/powermate/powermate_banner_mobile.png'

const sectionHeights: Record<string, SectionHeight> = {
    Experience: { desktop: 970, mobile: 1516 },
    Using: { desktop: 774, mobile: 1702 },
    Examples: { desktop: 1048, mobile: 1232.5 },
    Comparison: { desktop: 1268, mobile: 1094 },
    Compatibility: { desktop: 1002, mobile: 1462 },
    Order: { desktop: 1249, mobile: 1574 },
    Info: { desktop: 852, mobile: 836 },
}

const sections: Record<string, ComponentType<any>> = ['Using', 'Examples', 'Comparison'].reduce((acc, slug) => {
  acc[slug] = getComponent(() => import(`../components/Powermate/Powermate${slug}.tsx`))
  return acc
}, {} as Record<string, ComponentType<any>>)

const PowermateExperience = getComponent(() => import('../components/Powermate/PowermateExperience.tsx'))
const PowermateCompatibility = getComponent(() => import('../components/Powermate/PowermateCompatibility.tsx'))
const PowermateOrder = getComponent(() => import('../components/Powermate/PowermateOrder.tsx'))
const PowermateInfo = getComponent(() => import('../components/Powermate/PowermateInfo.tsx'))
const PowermateCompatibilityPopups = getComponent(() => import('../components/Popup/Powermate/PowermateCompatibilityPopups.tsx'), true)

const Powermate: FC = () => {
    const { getMinHeight } = useMinHeights(sectionHeights)

    const [forceRender, setForceRender] = useState<Record<string, boolean>>({ order: false })
    
    const onOrder = () => {
        scrollToElementLazy('shop-now', () => setForceRender(prev => ({ ...prev, order: true })))
    }

    const confirmCallbackRef = useRef<(() => void) | undefined>(undefined)
    const boostOrderCallbackRef = useRef<(() => void) | undefined>(undefined)    

    const [warningPopupOpen, setWarningPopupOpen] = useState<boolean>(false)
    const [checkPopupOpen, setCheckPopupOpen] = useState<boolean>(false)
    const [resultPopupOpen, setResultPopupOpen] = useState<boolean>(false)
    const [resultPopupType, setResultPopupType] = useState<CompatibilityResultPopupType>('default')
    
    const handleWarning = (confirmCallback?: () => void, boostOrderCallback?: () => void) => {
        setWarningPopupOpen(true)
        confirmCallbackRef.current = confirmCallback
        boostOrderCallbackRef.current = boostOrderCallback
    }

    const handleCheck = (boostOrderCallback?: () => void) => {
        setWarningPopupOpen(false)
        setCheckPopupOpen(true)
        if (!boostOrderCallbackRef.current) {
            boostOrderCallbackRef.current = boostOrderCallback
        }
    }
    
    const handleConfirm = () => {
        confirmCallbackRef.current?.()
        setWarningPopupOpen(false)
    }

    const handleComplete = (proceed: boolean) => {
        setCheckPopupOpen(false)
        const type: CompatibilityResultPopupType = proceed ? 'success' : 'warning'
        setResultPopupType(type)
        setResultPopupOpen(true)
    }

    const handleBoostOrder = () => {
        boostOrderCallbackRef.current?.()
        setResultPopupOpen(false)
        setResultPopupType('default')
    }    

    return (<>
        {PowermateCompatibilityPopups && (warningPopupOpen || checkPopupOpen || resultPopupOpen)  && (
            <LazySection minHeight="0px" forceRender={true}>
                <PowermateCompatibilityPopups {...{
                    warningPopupOpen, setWarningPopupOpen,
                    checkPopupOpen, setCheckPopupOpen,
                    resultPopupOpen, setResultPopupOpen,
                    handleWarning, handleCheck, handleConfirm, handleComplete, handleBoostOrder,
                    resultPopupType
                }} />
            </LazySection>
        )}
        <ProductLayout className="powermate" onOrder={onOrder}>
            <PowermateBanner {...{bannerDesktopBackground, bannerMobileBackground}} onOrder={onOrder} />
            <LazySection minHeight={getMinHeight('Experience')} rootMargin="-10px">
                <PowermateExperience />
            </LazySection>
            {Object.entries(sections).map(([slug, Component]) => (
                <LazySection key={slug} minHeight={getMinHeight(slug)}>
                    <Component />
                </LazySection>
            ))}
            <LazySection minHeight={getMinHeight('Compatibility')}>
                <PowermateCompatibility onCompatibilityCheck={() => handleCheck(onOrder)} />
            </LazySection>
            <LazySection id="shop-now" minHeight={getMinHeight('Order')} forceRender={forceRender.order}>
                <PowermateOrder onCompatibilityWarning={handleWarning} />
            </LazySection>
            <LazySection minHeight={getMinHeight('Info')}>
                <PowermateInfo />
            </LazySection>
        </ProductLayout>
    </>)
}

mountForShopify('react-powermate-root', Powermate)

export { PowermateBanner }
export default Powermate
